import { useCallback } from 'react';
import type { AdminPhotoFilter } from '@mantelpiece/shared';
import { api } from '../lib/api';
import { go } from '../lib/route';
import { useLoad } from '../lib/useLoad';
import { Loading, Pager, Search } from '../components/bits';
import { PhotoGrid } from '../components/PhotoGrid';
import { PageHead } from '../components/Shell';

interface Props {
  readonly token: string;
  readonly query: URLSearchParams;
}

const FILTERS: readonly { readonly value: AdminPhotoFilter; readonly label: string }[] = [
  { value: 'live', label: 'On frames or queued' },
  { value: 'approved', label: 'On frames' },
  { value: 'pending', label: 'Awaiting approval' },
  { value: 'deleted', label: 'Taken down' },
  { value: 'all', label: 'Everything' },
];

const isFilter = (value: string): value is AdminPhotoFilter =>
  FILTERS.some((option) => option.value === value);

export const Photos = ({ token, query }: Props) => {
  const q = query.get('q') ?? '';
  const rawFilter = query.get('filter') ?? 'live';
  const filter: AdminPhotoFilter = isFilter(rawFilter) ? rawFilter : 'live';
  const deviceId = query.get('deviceId') ?? '';
  const accountId = query.get('accountId') ?? '';
  const assetId = query.get('assetId') ?? '';
  const offset = Number(query.get('offset')) || 0;

  const params = { q, filter, deviceId, accountId, assetId };
  const loaded = useLoad(
    () => api.listPhotos(token, { ...params, offset, limit: 48 }),
    [token, q, filter, deviceId, accountId, assetId, offset],
  );

  const onSearch = useCallback(
    (next: string) => go('/photos', { ...params, q: next }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, deviceId, accountId, assetId],
  );

  const scoped = deviceId || accountId || assetId;

  return (
    <>
      <PageHead
        eyebrow="Photos"
        title="Every placement"
        lede={
          <>
            A photo is a picture sitting on one frame. The same picture on two frames is two rows here and one
            set of bytes.
            {scoped && (
              <>
                {' '}
                Showing a subset —{' '}
                <a href="#/photos" onClick={(event) => { event.preventDefault(); go('/photos', { filter }); }}>
                  show everything
                </a>
                .
              </>
            )}
          </>
        }
        actions={
          <>
            <Search value={q} placeholder="Caption, place, sender or frame" onSearch={onSearch} />
            <label className="field field-inline">
              <span className="visually-hidden">Which photos</span>
              <select
                className="select"
                value={filter}
                onChange={(event) => go('/photos', { ...params, filter: event.target.value })}
              >
                {FILTERS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </>
        }
      />

      <Loading loaded={loaded}>
        {(page) => (
          <>
            <PhotoGrid token={token} photos={page.items} onChanged={loaded.reload} />
            <Pager page={page} noun="photos" onOffset={(next) => go('/photos', { ...params, offset: next })} />
          </>
        )}
      </Loading>
    </>
  );
};
